import { CollectionType, type Collection } from '@/types/collection';
import { createFileRoute, type SearchSchemaInput } from '@tanstack/react-router';
import { useState, useEffect, useRef } from 'react';
import { trainingAttemptSession } from '../store/trainingAttemptSession';
import { useGetTrainingAttempt } from '../hooks/trainingAttempts/useTrainingAttempts';
import { useTrainingAttemptManager } from '../hooks/trainingAttempts/useTrainingAttemptManager';
import SimulationExecutionDefault from '../components/SimulationFlow/SimulationExecutionDefault';
import SimulationResults from '../components/SimulationFlow/SimulationResults';
import type { ApiTrainingAttempt } from '../api/trainingAttempts.api';
import type { ApiSubmitResult } from '../api/simulations.api';

import Loader from '../components/Loader/Loader';
import GeneralError from '../components/Errors/General';

export const Route = createFileRoute('/esercitazioni/$id')({
  validateSearch: (search: { from?: string } & SearchSchemaInput): { from: string } => ({
    from: typeof search.from === 'string' ? search.from : 'esercitazioni',
  }),
  component: RouteComponent,
});

function makeStubCollection(name: string): Collection {
  return {
    _id: '',
    type: CollectionType.EXERCISE,
    name,
    attributes: {},
    status: 'active',
    archived: false,
    timed: false,
    createdAt: '',
    updatedAt: '',
    sections: [{ rules: { pausable: true }, questions: [] }],
  };
}

function TrainingAttemptExecution({ initialAttempt, backPath }: { initialAttempt: ApiTrainingAttempt; backPath: string }) {
  const [submitResult, setSubmitResult] = useState<ApiSubmitResult | null>(null);
  const manager = useTrainingAttemptManager({ initialAttempt });
  const collection = makeStubCollection(initialAttempt.name);

  useEffect(() => {
    if (submitResult) trainingAttemptSession.set(manager.getEnrichedAttempt());
  }, [!!submitResult]);

  // On unmount without submit, persist current answers to session so resume restores them
  const submittedRef = useRef(false);
  submittedRef.current = !!submitResult;
  useEffect(() => {
    return () => {
      if (!submittedRef.current) {
        trainingAttemptSession.set(manager.getEnrichedAttempt());
      }
    };
  }, []);

  useEffect(() => {
    if (submitResult) return;
    const handler = (e: BeforeUnloadEvent) => e.preventDefault();
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [!!submitResult]);

  // Training attempts are always pool-based custom exercises (as opposed to
  // a predefined EXERCISE collection, which flows through /simulazioni/$id).
  const isCustomExercise = true;

  if (submitResult && manager.attempt) {
    return (
      <SimulationResults
        result={submitResult}
        collection={collection}
        attempt={manager.attempt}
        backPath={backPath}
        correctionPath={`/esercitazioni/${initialAttempt._id}/correzione`}
        isCustomExercise={isCustomExercise}
      />
    );
  }

  return (
    <SimulationExecutionDefault
      collection={collection}
      manager={manager}
      onSubmitSuccess={setSubmitResult}
      isCustomExercise={isCustomExercise}
    />
  );
}

function RouteComponent() {
  const { id } = Route.useParams();
  const { from } = Route.useSearch();

  const sessionAttempt = trainingAttemptSession.get();
  const hasSession = sessionAttempt?._id === id;

  const { attempt: fetchedAttempt, isFetching, isError } = useGetTrainingAttempt(
    hasSession ? '' : id,
  );

  const attempt = hasSession ? sessionAttempt : fetchedAttempt;

  if (!hasSession && isFetching && !attempt) return <Loader fullScreen />;
  if (!hasSession && (isError || !attempt)) return <GeneralError />;
  if (!attempt) return <GeneralError />;

  return <TrainingAttemptExecution initialAttempt={attempt} backPath={`/${from}`} />;
}
